import React, { PropsWithChildren, useLayoutEffect, useRef } from "react";

import "./busy-blocker.css";
import { useFocusLock } from "../../hooks/useFocusLock";
import { useKeyTrap } from "../../hooks/useKeyboard";

declare module "react" {
	interface CSSProperties {
		"--busy-blocker-delay"?: string;
	}
}

export const BusyBlocker = ({
	isOpen,
	delayInMs: delay = 300,
}: PropsWithChildren<{
	isOpen: boolean;
	/**
	 * Delay to show the busy blocker
	 * UI will be blocked but not yet visible
	 */
	delayInMs?: number;
}>) => {
	// We want to use a transition delay but that does not work on component mount
	// therefore we set it by javascript when drawn
	// Remark: This makes it not possible to use as server component
	const elRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const el = elRef.current;
		if (!el) return;

		if (isOpen) {
			el.classList.add("busy-blocker--open");
		} else {
			el.classList.remove("busy-blocker--open");
		}
	}, [isOpen]);

	useFocusLock(elRef, {
		isActive: isOpen,
	});

	// Prevent keystrokes bubble up to the parent elements
	useKeyTrap(elRef, {
		enabled: isOpen,
	});

	return (
		<>
			<div
				ref={elRef}
				className={`busy-blocker`}
				style={{ "--busy-blocker-delay": `${delay}ms` }}
				tabIndex={-1}
			>
				<div className="busy-blocker__dots">
					<div className="busy-blocker__dot" />
					<div className="busy-blocker__dot" />
					<div className="busy-blocker__dot" />
					<div className="busy-blocker__dot" />
				</div>
			</div>
		</>
	);
};

BusyBlocker.displayName = "BusyBlocker";
